// useDataTableTechnicalColumns.ts
import { computed, type ComputedRef, type Ref } from 'vue';

type TechnicalDeps = {
  /**
   * Whether the checkbox selection column is rendered.
   * When enabled, it contributes a leading technical column.
   */
  checkboxMultiselect: Ref<boolean>;

  /**
   * Whether the table supports expanded rows in general.
   * May be a computed/Ref (to allow reactive feature toggling).
   */
  hasExpandedRow: ComputedRef<boolean> | Ref<boolean>;

  /**
   * Whether the expander column is rendered in "collapsed" mode (i.e. with an explicit expander column).
   * When true (together with hasExpandedRow), it contributes a leading technical column.
   */
  collapsedExpandedRow: ComputedRef<boolean> | Ref<boolean>;

  /**
   * Runtime toggle for PrimeVue row-reorder feature.
   * When enabled (and no sort/filter is active), it contributes a leading technical column.
   */
  reorderableRowsEnabled: Ref<boolean>;

  /**
   * Current multi-sort meta. Row reordering is typically disabled when sorting is active.
   */
  currentSortMeta: Ref<any[]>;

  /**
   * Current quick-filter string. Row reordering is typically disabled when filtering is active.
   */
  savedFilterString: Ref<string>;
};

/**
 * Technical columns helper for PrimeVue DataTable.
 *
 * "Technical columns" are non-data columns rendered before the actual data columns, e.g.:
 * - Row reorder handle column
 * - Checkbox selection column
 * - Expander column
 *
 * Responsibilities:
 * - Compute how many technical columns are currently rendered (dynamic, based on state).
 * - Provide utilities to translate a clicked <td> index into a data-column index.
 * - Provide a predicate to ignore clicks on technical columns in row/cell activation logic.
 *
 * Why it matters:
 * PrimeVue renders these columns in the same <tr> as data cells. If you want keyboard navigation
 * or click-to-edit behavior, you must compensate for the offset introduced by technical columns.
 */
export function useDataTableTechnicalColumns(deps: TechnicalDeps) {
  const {
    checkboxMultiselect,
    hasExpandedRow,
    collapsedExpandedRow,
    reorderableRowsEnabled,
    currentSortMeta,
    savedFilterString,
  } = deps;

  /**
   * Whether the row-reorder handle column is currently visible.
   * Common policy: only allow row reorder when there is no active sort and no active filter.
   */
  const hasRowReorderColumn = computed(() => {
    return (
      reorderableRowsEnabled.value &&
      currentSortMeta.value.length === 0 &&
      savedFilterString.value.length === 0
    );
  });

  /** Whether the checkbox selection column is currently visible. */
  const hasCheckboxColumn = computed(() => !!checkboxMultiselect.value);

  /**
   * Whether the expander column is currently visible.
   * PrimeVue uses an explicit expander column only when expanded rows are enabled and the UI is collapsed.
   */
  const hasExpanderColumn = computed(() => {
    // Both deps can be computed refs or normal refs; `.value` always resolves.
    const exp = hasExpandedRow.value;
    const col = collapsedExpandedRow.value;
    return !!exp && !!col;
  });

  /**
   * Total number of leading technical columns currently rendered in each body row.
   * This is the offset you must subtract from a clicked <td> index to reach `visibleColumns`.
   */
  const technicalColumnCount = computed(() => {
    return (
      Number(hasRowReorderColumn.value) +
      Number(hasCheckboxColumn.value) +
      Number(hasExpanderColumn.value)
    );
  });

  /**
   * Determines the index of the clicked body cell (<td>) within its row (<tr>).
   *
   * Returns:
   * - 0..N for a valid <td> in the row
   * - -1 if the click did not happen inside a body <td>
   *
   * Note:
   * This counts *all* cells in the row, including technical columns.
   */
  function getBodyCellIndexFromEvent(originalEvent: MouseEvent): number {
    const td = (originalEvent.target as HTMLElement | null)?.closest('td');
    if (!td) return -1;

    const tr = td.parentElement;
    if (!tr) return -1;

    const allTds = Array.from(tr.children);
    return allTds.indexOf(td);
  }

  /**
   * Checks whether the click occurred inside one of the leading technical columns.
   * Use this to skip edit activation / row selection logic for reorder/checkbox/expander cells.
   */
  function isClickOnTechnicalColumn(originalEvent: MouseEvent): boolean {
    const cellIndex = getBodyCellIndexFromEvent(originalEvent);
    if (cellIndex < 0) return false;
    return cellIndex < technicalColumnCount.value;
  }

  /**
   * Converts a body <td> index into a "visible column" index (data columns only).
   *
   * Example:
   * - If there are 2 technical columns, bodyCellIndex=2 corresponds to visibleColumnIndex=0.
   *
   * Returns negative values if called with a technical column index; callers should guard.
   */
  function toVisibleColumnIndex(bodyCellIndex: number): number {
    return bodyCellIndex - technicalColumnCount.value;
  }

  return {
    technicalColumnCount,
    hasRowReorderColumn,
    hasCheckboxColumn,
    hasExpanderColumn,
    getBodyCellIndexFromEvent,
    isClickOnTechnicalColumn,
    toVisibleColumnIndex,
  };
}
